/* global  */

/**
 * K2 Inc. Confidential
 *
 * Copyright © 2015 K2 Inc. All rights reserved.
 *
 * Notice: All information contained herein is, and remains the property of K2 Inc. and its
 * suppliers, if any. The intellectual and technical concepts contained herein are proprietary to K2
 * Inc. and/or its suppliers, and may be covered by U.S. and foreign patents or patents in process,
 * and are protected by trade secret or copyright law. Dissemination of this information or
 * reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from K2 Inc.
 */

import Kefir from "kefir";
// import clone from "clone";
import Channel from "./Channel";


const ObjectToString = Object.prototype.toString,
    /**
     * Get the type of a given object
     * @param {Object|Array|Other} that The type of the object one of:
     * @return {String} The type of the object
     */
    getTypeOf = that => {
      // why 8? cause the result is always of pattern: [object <type>]
      return ObjectToString.call(that).slice(8, -1);
    },

    /**
     * Recursively copy an object into to return a new object. Handles nested structures like arrays
     * and objects
     * Warning!! Does not handle circular references
     * @param {Any} obj
     * @param {boolean} deep Whether to deep copy
     * @return {Any} The newly copied object
     */
    clone = (obj, deep) => {
      const type = getTypeOf(obj);
      let copy = null;
      switch (type) {
        case "Object":
          if(obj != null) {
            copy = {};
            Object.keys(obj).forEach(k => {
              copy[k] = deep ? clone(obj[k], deep) : obj[k];
            });
          }
          break;
        case "Array":
          copy = obj.map((o, i) => {
            return deep ? clone(o, deep) : o;
          });
          break;
        case "Date":
          copy = new Date(obj.getTime());
          break;
        default:
          copy = obj;
          break;
      }
      return copy;
    },

    /**
     * Creates a producer for a specified channel. This emits data events
     * @param {Channel} source_channel  The channel to wrap
     * @return {Producer} A producer object with start, emit and done functions
     */
    createChannelProducer = source_channel => {
      let listener;
      const channel = source_channel,
          name = channel.name,
          subscriptions = [],

          start = function(emitter) {
            listener = emitter;
            subscriptions.push(channel.on("data", (event, data) => {
              listener.value(data);
            }));
            subscriptions.push(channel.on("error", (event, data) => {
              listener.error(data);
            }));
            channel.open();
            // console.log("[ChannelProducer] Started", name);

            // Stopping logic
            return () => {
              // channel.close();
              done();
              // console.log("[ChannelProducer] Stopped", name);
            };
          },
          emit = function(event, data) {
            channel.emit(event, data);
          },
          done = function() {
            subscriptions.forEach(sub => sub.dispose());
            subscriptions.splice(0);
            channel.close();
            if(listener) {
              listener.end();
              listener = null;
            }
            // console.log("[ChannelProducer] Done", name);
          };

      return {
        channel,
        start,
        emit,
        done
      };
    },

    /**
     * Can create a new channel that delegates calls to a target created by a factory function
     * e.g.
     * new ProxyChannel('some', {config: "value"}, config => {})
     */
    ProxyChannel = Channel.define({
      _initialize(name, config, factory) {
        const self = this;
        this.context = {
          config: config,
          emit: function(...args) {
            self.emit(...args);
          }
        };
        // call the factory to create the channel
        this.target = factory(config);
      },
      open() {
        this.target.open(this.context);
      },
      update() {
        this.target.update && this.target.update(this.context);
      },
      close() {
        this.target.close();
      }
    }),

    createDynamicChannel = channel_name => {
      return {
        name: channel_name,
        factory: () => {
          return {
            open: function(context) {},
            close: function() {}
          };
        }
      };
    };



Channel.register("extended", {
  open(context) {
    // console.log(context);
    const {datalayer, from, transform = data => data} = this.config;
    this.subscription = datalayer.subscribe(
      from,
      // onData
      data => this.emit("data", transform(data)),
      // onError
      err => this.emit("error", err),
      // onComplete
      data => this.emit("complete")
    );
  },
  close() {
    this.subscription && this.subscription.unsubscribe();
  }
});


const DataLayer = function(dlName) {
  const name = dlName,
      all_channels = {},
      createStreamFor = channel_name => {
        return all_channels[channel_name].stream.map(packet => {
          // console.log("[Subs-Stream] Packet", packet);
          // return clone(packet);
          return packet;
        });
      };

  return {
    getName() {
      return name;
    },

    /**
     * Creates a new channel from specified info object. The info object is secified as follows:
     * <pre>
     * {
     *   name: "/my/channel",
     *   type: "http | sse ",
     *   factory: <custom function that returns an object with open and close functions
     *   config: {
     *     // for http and https ----------------------------------------------
     *     url: "http://graphite/render",         // The URL to fetch from
     *     method: "GET",                         // HTTP method usually, GET or POST,
     *                                               defaults to GET
     *     data: {some: "data"},                  // Data to be sent with the request
     *     params: {                              // params will be apppended to querystring
     *       target: "average(server.web*.load)",
     *       from: "-30min",
     *       format: "json",
     *       actions: ["add", "edit"]             // This will be: actions=add&actions=edit, etc.
     *     },
     *     headers: {},
     *     interval: 3000                         // Interval to make this http request
     *
     *     // for SSE ------------------------------------------------------------
     *     url: "http://some/url/",
     *     events: "space separated events"
     *   }
     * }
     * </pre>
     * @param {Object} info The channel info object.
     * @return {Stream} A newly created channel
     */
    createChannel(info) {
      const {name: channel_name, type, factory} = info, config = clone(info.config || {});
      let channel;

      if(all_channels[channel_name]) {
        throw new Error("Channel already exists: " + channel_name);
      }

      config.channel = channel_name;
      config.datalayer = this;

      if(type) {
        const ChannelType = Channel.getByType(type);
        if(!ChannelType) {
          throw new Error("No such Channel type " + type);
        }
        channel = new ChannelType(channel_name, config);
      }else if(factory && typeof factory === "function") {
        // a custom factory for publishing data to channel
        channel = new ProxyChannel(channel_name, config, factory);
      }else {
        throw new Error("Create channel requires either a type or a factory function");
      }


      const producer = createChannelProducer(channel), stream = Kefir.stream(producer.start);

      all_channels[channel_name] = {
        producer: producer,
        stream: stream // ,
        // channel: channel
      };
      // console.log("[Datalayer] Created channel", channel_name);
      return stream.map(packet => packet);
    },

    updateChannel(name, config) {
      const channel_info = all_channels[name];
      if(!channel_info) {
        throw new Error(`No such channel: ${name}`);
      }
      const {producer: {channel}} = channel_info;
      channel.update(config);
    },

    /**
     * Shuts down and destroys the channel, disposing all its listeners
     * @param {String} name The channel name
     */
    destroyChannel(name) {
      const channel_info = all_channels[name];
      if(!channel_info) {
        console.warn("No such channel, ", name);
        return;
      }

      delete all_channels[name];
      channel_info.producer.done();
    },

    /**
     * Destroys all the channels in this datalayer instance
     */
    destroyAllChannels() {
      Object.keys(all_channels).forEach(ch => {
        this.destroyChannel(ch);
      });
    },

    /**
     * Publishes data to the channel specified by channelName
     * @param {String}  channel_name The name of the channel. If it does not exist,
     *                              a new one will be created
     * @param {Object}  data        The data to publish
     * @param {boolean} is_error     True if you want to publish an error on the specified channel
     */
    publish(channel_name, data, is_error) {
      let channel_info = all_channels[channel_name];
      if(!channel_info) {
        this.createChannel(createDynamicChannel(channel_name));
        channel_info = all_channels[channel_name];
      }
      // const channel = channelInfo.channel;
      // channel.emit((isError ? "error" : "data"), data);
      const producer = channel_info.producer;
      producer.emit(is_error ? "error" : "data", data);
    },

    /**
     * Subscribes to the channel specified by 'channelName'
     * @param {String}    channel_name The name of the channel
     * @param {Function}  onData      The callback to call when data arrives at the channel
     * @param {Function}  onError      The callback to call when data arrives at the channel
     * @param {Function}  onComplete      The callback to call when data arrives at the channel
     * @return {Object}               A subscription object with a 'dispose()' method that can be
     *                                called to unsubscribe from this channel
     */
    subscribe(channel_name, onData, onError, onComplete) {
      let channelInfo = all_channels[channel_name];
      if(!channelInfo) {
        this.createChannel(createDynamicChannel(channel_name));
        channelInfo = all_channels[channel_name];
      }

      const subscription = this.getChannelStream(channel_name).observe(onData, onError, onComplete);
      // subscription.channelName = channelName;
      return subscription;
    },

    /**
     * Gets an observable backed by this channel
     * @param   {String} channel_name The name of the channel
     * @return {Stream} The observable backed by the channel name
     */
    getChannelStream(channel_name) {
      return all_channels[channel_name] ? createStreamFor(channel_name) : null;
    }

  };
};


export default DataLayer;
